import {
  CSSProperties,
  FC,
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  SVGProps,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './StudyInfoPageLayout.module.scss';
import { Loading } from 'uikit/suspense/Loading';
import clsx from 'clsx';
import { AppButton } from 'uikit/buttons';
import { ReactComponent as Arrow } from 'src/assets/img/common/b_arrow_24.svg';
import React from 'react';
import debounce from 'lodash/debounce';

type StudyInfoPageLayoutProps = PropsWithChildren<{
  isLoading?: boolean;
  testId?: string;
}>;

type HeaderProps = PropsWithChildren<{
  className?: string;
  style?: CSSProperties;
  testId?: string;
}>;

type ColumnProps = PropsWithChildren<{
  colored?: boolean;
}>;
type ColumnsProps = PropsWithChildren;

export const StudyInfoPageLayout: FC<StudyInfoPageLayoutProps> & {
  Header: FC<HeaderProps> & {
    Filters: FC<PropsWithChildren>;
  };
  Columns: FC<ColumnsProps> & {
    Column: FC<ColumnProps>;
  };
} = ({ isLoading, testId, children }) => {
  const loadingTestId = testId ?? 'study-info-page-layout';

  return (
    <Loading loading={!!isLoading} containerClassName={styles.scrollWrapper} testId={loadingTestId}>
      <div data-test-id={testId} className={styles.scrollWrapper}>
        {children}
      </div>
    </Loading>
  );
};

const Header: typeof StudyInfoPageLayout.Header = ({ children, className, style, testId }) => (
  <div className={clsx(styles.header, className)} style={style} data-test-id={testId}>
    {children}
  </div>
);

Header.Filters = ({ children }) => <div className={styles.filters}>{children}</div>;

StudyInfoPageLayout.Header = Header;

const Columns: typeof StudyInfoPageLayout.Columns = ({ children }) => <div className={styles.columns}>{children}</div>;

Columns.Column = ({ children, colored }) => (
  <div className={styles.column} data-colored={!!colored}>
    {children}
  </div>
);

StudyInfoPageLayout.Columns = Columns;

type StudyInfoPageSectionProps = PropsWithChildren<{
  title: string;
  subTitle?: string | JSX.Element;
  isLoading?: boolean;
  rightHeader?: ReactNode;
  contentClassName?: string;
  variant?: 'block' | 'flex';
  isFullHeight?: boolean;
  placeholderProps?: PlaceholderProps;
}>;

export const StudyInfoPageSection: FC<StudyInfoPageSectionProps> = ({
  title,
  rightHeader,
  children,
  contentClassName,
  isLoading,
  placeholderProps,
  subTitle,
  isFullHeight,
  variant = 'block',
}) => {
  const [expanded, setExpanded] = useState(true);

  const { containerRef, maxCardWidth, setMeasurerRef } = useMaxContentWidth(children);

  const flexContent = useMemo(
    () => (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${maxCardWidth}px, 1fr))`,
          gap: '16px',
          width: '100%',
          position: 'relative',
        }}
        ref={containerRef}
      >
        {React.Children.map(children, (child, index) => (
          <React.Fragment key={index}>
            {/* NOTE: Measurer layer */}
            <div ref={setMeasurerRef} aria-hidden={'true'} className={styles.measurerHidden}>
              {child}
            </div>
            <div className={styles.cardVisibleContent}>{child}</div>
          </React.Fragment>
        ))}
      </div>
    ),
    [children, containerRef, maxCardWidth, setMeasurerRef],
  );

  return (
    <div
      className={clsx(styles.section, {
        [styles.fullHeight]: isFullHeight && expanded,
      })}
    >
      <div className={styles.sectionHeader}>
        <div className={styles.title}>{title}</div>
        <div className={styles.sectionRightHeader}>
          <div>{rightHeader}</div>
          <AppButton
            className={clsx(styles.collapseButton, { [styles.collapsed]: !expanded })}
            variant={'icon-link'}
            colorSchema={'basic'}
            Icon={Arrow}
            onClick={() => setExpanded((x) => !x)}
          />
        </div>
      </div>
      {expanded && (
        <div className={styles.collapseContainer}>
          <Loading
            loading={!!isLoading}
            containerClassName={clsx(styles.content, contentClassName)}
            testId={'study-info-page-section'}
          >
            {subTitle && !placeholderProps?.showPlaceholder && <div className={styles.subtitle}>{subTitle}</div>}
            <WrapperWithBlur className={clsx({ [styles.overflowHidden]: !expanded })}>
              <Placeholder {...placeholderProps}>{variant === 'flex' ? flexContent : children}</Placeholder>
            </WrapperWithBlur>
          </Loading>
        </div>
      )}
    </div>
  );
};

export const StudyInfoCard: FC<PropsWithChildren<{ title?: string | JSX.Element; className?: string }>> = ({
  children,
  className,
  title,
}) => {
  return (
    <div className={clsx(styles.card, className)}>
      <div className={styles.titleWithValue}>
        {title && <div className={styles.cardTitle}>{title}</div>}
        {children}
      </div>
    </div>
  );
};

export const CardValue: FC<{ mainText: string; secondText?: string }> = ({ mainText, secondText }) => {
  return (
    <span className={styles.cardValue}>
      <span>{mainText}</span>
      {secondText && <span>{secondText}</span>}
    </span>
  );
};

type UseMaxContentWidthResult = {
  containerRef: React.RefObject<HTMLDivElement>;
  setMeasurerRef: (el: HTMLSpanElement | null) => void;
  maxCardWidth: number;
};

export const useMaxContentWidth = (children: ReactNode): UseMaxContentWidthResult => {
  const [maxWidth, setMaxWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const measurersRef = useRef<Set<HTMLSpanElement>>(new Set());
  const setMeasurerRef = useCallback((el: HTMLSpanElement | null) => {
    if (el) measurersRef.current.add(el);
    else measurersRef.current.delete(el as any);
  }, []);

  useLayoutEffect(() => {
    const measure = debounce(() => {
      let max = 0;
      measurersRef.current.forEach((el) => {
        if (el) {
          const width = el.scrollWidth;
          if (width > max) max = width;
        }
      });

      const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;
      setMaxWidth(Math.min(max + 1, containerWidth));
    }, 20);

    const observer = new ResizeObserver(measure);
    if (containerRef.current) observer.observe(containerRef.current);

    measure();
    return () => observer.disconnect();
  }, [children]);

  return { containerRef, setMeasurerRef, maxCardWidth: maxWidth };
};

export const WrapperWithBlur: FC<PropsWithChildren<{ className?: string }>> = ({ children, className }) => {
  const [topReached, setTopReached] = useState(false);
  const [bottomReached, setBottomReached] = useState(false);

  const topRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === topRef.current) setTopReached(entry.isIntersecting);
          if (entry.target === bottomRef.current) setBottomReached(entry.isIntersecting);
        });
      },
      { threshold: 0 },
    );

    [topRef, bottomRef].forEach((ref) => ref.current && observer.observe(ref.current));
    return () => observer.disconnect();
  }, []);

  return (
    <div data-at-top={topReached} data-at-bottom={bottomReached} className={clsx(styles.blur, className)}>
      <div ref={topRef} className={styles.anchor} />
      {children}
      <div ref={bottomRef} className={styles.anchor} />
    </div>
  );
};

type PlaceholderProps = {
  showPlaceholder?: boolean;
  Icon?: FunctionComponent<SVGProps<any>>;
  text?: string;
  className?: string;
};

export const Placeholder: FC<PropsWithChildren<PlaceholderProps>> = ({
  showPlaceholder,
  Icon,
  text,
  children,
  className,
}) => {
  if (!showPlaceholder) return <>{children}</>;

  return (
    <div className={clsx(styles.placeholder, className)}>
      {Icon && <Icon />}
      {text && <div>{text}</div>}
    </div>
  );
};
